import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ConflictModal = ({ 
  isOpen, 
  onClose, 
  conflicts, 
  onResolveConflict, 
  onIgnoreConflict 
}) => {
  if (!isOpen) return null;

  const formatTime = (hour) => {
    return `${hour?.toString()?.padStart(2, '0')}:00`;
  };

  const getConflictSeverity = (conflict) => {
    if (conflict?.type === 'confirmed_shift') return 'high';
    if (conflict?.type === 'pending_shift') return 'medium';
    return 'low';
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'text-error';
      case 'medium': return 'text-warning';
      default: return 'text-muted-foreground';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'high': return 'AlertTriangle';
      case 'medium': return 'AlertCircle';
      default: return 'Info';
    }
  };

  const getConflictTypeLabel = (type) => {
    switch (type) {
      case 'confirmed_shift': return 'Bestätigte Schicht';
      case 'pending_shift': return 'Ausstehende Schicht';
      case 'recurring_pattern': return 'Wiederkehrendes Muster';
      default: return 'Unbekannter Konflikt';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-card rounded-lg border border-border shadow-modal max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Icon name="AlertTriangle" size={24} className="text-warning" />
              <div>
                <h2 className="text-xl font-semibold text-foreground">
                  Verfügbarkeitskonflikte erkannt
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {conflicts?.length} Konflikt{conflicts?.length !== 1 ? 'e' : ''} gefunden
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
            >
              <Icon name="X" size={20} />
            </Button>
          </div>
        </div>

        {/* Conflicts List */}
        <div className="p-6 max-h-96 overflow-y-auto">
          <div className="space-y-4">
            {conflicts?.map((conflict, index) => {
              const severity = getConflictSeverity(conflict);
              return (
                <div key={index} className="border border-border rounded-lg p-4 bg-muted">
                  <div className="flex items-start space-x-3">
                    <Icon 
                      name={getSeverityIcon(severity)} 
                      size={20} 
                      className={`mt-0.5 ${getSeverityColor(severity)}`} 
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-medium text-foreground">
                          {conflict?.day}, {formatTime(conflict?.startHour)} - {formatTime(conflict?.endHour)}
                        </h3>
                        <span className={`text-xs px-2 py-1 rounded-full bg-background ${getSeverityColor(severity)}`}>
                          {getConflictTypeLabel(conflict?.type)}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">
                        {conflict?.description}
                      </p>
                      
                      {/* Conflict Details */}
                      <div className="bg-background rounded p-3 mb-3">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-muted-foreground">Aktuelle Verfügbarkeit:</span>
                            <span className="ml-2 font-medium text-foreground">
                              {conflict?.currentAvailability}
                            </span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Neue Verfügbarkeit:</span>
                            <span className="ml-2 font-medium text-foreground">
                              {conflict?.newAvailability}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Suggested Actions */}
                      {conflict?.suggestions && conflict?.suggestions?.length > 0 && (
                        <div className="mb-3">
                          <h4 className="text-sm font-medium text-foreground mb-2">
                            Lösungsvorschläge:
                          </h4>
                          <ul className="text-sm text-muted-foreground space-y-1">
                            {conflict?.suggestions?.map((suggestion, idx) => (
                              <li key={idx} className="flex items-start space-x-2">
                                <Icon name="ArrowRight" size={12} className="mt-1 text-primary" />
                                <span>{suggestion}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onResolveConflict(conflict, 'reschedule')}
                          iconName="Calendar"
                          iconPosition="left"
                          iconSize={14}
                        >
                          Umplanen
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onResolveConflict(conflict, 'notify')}
                          iconName="Bell"
                          iconPosition="left"
                          iconSize={14}
                        >
                          Benachrichtigen
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onIgnoreConflict(conflict)}
                          className="text-muted-foreground"
                        >
                          Ignorieren
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border bg-muted">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              <Icon name="Info" size={14} className="inline mr-1" />
              Konflikte müssen vor dem Speichern gelöst werden
            </div>
            <div className="flex space-x-2">
              <Button
                variant="outline"
                onClick={onClose}
              >
                Abbrechen
              </Button>
              <Button
                variant="default"
                onClick={() => {
                  // Resolve all conflicts automatically
                  conflicts?.forEach(conflict => {
                    onResolveConflict(conflict, 'auto');
                  });
                }}
                iconName="CheckCircle"
                iconPosition="left"
                iconSize={16}
              >
                Alle automatisch lösen
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConflictModal;